import type { ApolloError } from '@apollo/client';
import { useTheme } from '@emotion/react';
import type { ButtonProps } from '@hover/blueprint';
import {
  Body,
  Box,
  Button,
  HStack,
  Heading,
  Spacer,
  useToast,
} from '@hover/blueprint';
import type { GraphQLError } from 'graphql';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { generatePath, useHistory } from 'react-router';
import type { InferType } from 'yup';

import { DesignerPartner } from 'src/modules/homeownerApp/constants/partners';
import {
  usePartner,
  usePartnerOrgId,
} from 'src/modules/homeownerApp/hooks/usePartner';
import { BackButton } from 'src/modules/onboarding/components/BackButton';
import { makeHomeownerSignupValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { LinkExternal } from 'src/shared/components';
import { PasswordInputWithValidation } from 'src/shared/components/PasswordInputWithValidation/PasswordInputWithValidation';
import { SkipSSR } from 'src/shared/components/SSR/SkipSSR';
import {
  Form as SharedForm,
  TextField as SharedTextField,
} from 'src/shared/components/forms';
import type { OnboardingCreateHomeownerUserMutation } from 'src/shared/graphql/generated';
import {
  useCreateProjectMutation,
  useOnboardingCreateHomeownerUserMutation,
} from 'src/shared/graphql/generated';
import { useRefreshSession } from 'src/shared/graphql/hooks';
import { useTranslate } from 'src/shared/hooks';
import { useSendCustomHubSpotEvent } from 'src/shared/hooks/hubspot';
import { usePostData } from 'src/shared/hooks/usePostData';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { External, Route, Route as Routes } from 'src/shared/route';
import { Page, accountCreated, buttonPressed } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';
import { baseUrlToRedirect } from 'src/shared/utils/url';

export type PasswordFormData = InferType<
  ReturnType<typeof makeHomeownerSignupValidationSchema>
>;

export interface PasswordFieldProps extends Pick<ButtonProps, 'isLoading'> {}

const EMAIL_IS_ALREADY_ASSOCIATED =
  'Email address is already associated with a HOVER account.';

const Fields = ({ isLoading }: PasswordFieldProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { email, fullName } = usePostData();
  const { breakpoints } = useTheme();
  const history = useHistory();
  const [isInvalid, setIsInvalid] = useState(true);
  const goBack = () => history.goBack();

  const { partner } = usePartner();
  const isPella = partner === DesignerPartner.PELLA;

  const headingText = isPella
    ? t('onboarding.pella.createAnAccount')
    : t('onboarding.createAnAccount');
  const bodyText = isPella
    ? t('onboarding.pella.weJustNeedAFewPieces')
    : t('onboarding.weJustNeedAFewPieces');

  return (
    <>
      <HStack
        alignItems="center"
        flexDirection="column"
        flexGrow={1}
        justifyContent="start"
        marginTop={300}
        spacing={0}
        width={1}
      >
        <Heading
          color="text.primary"
          marginBottom="16px !important"
          maxWidth={850}
          size={{ base: 600, tablet: 900 }}
          textAlign={{ base: 'left', tablet: 'center' }}
          width={1}
        >
          {headingText}
        </Heading>
        <Body
          as={Box}
          color="neutralText"
          justifyContent={{ base: 'start', tablet: 'center' }}
          opacity={0.64}
          size="small"
          width={{ base: 1, tablet: '500px' }}
        >
          {bodyText}
        </Body>
        <Box
          flexDirection="column"
          flexGrow={1}
          width={{ base: 1, tablet: '500px' }}
        >
          <SharedTextField
            defaultValue={fullName}
            id="name"
            label={t('onboarding.fullName')}
            name="name"
            paddingTop={500}
            placeholder=""
            type="text"
          />
          <SharedTextField
            defaultValue={email}
            id="email"
            label={t('onboarding.email')}
            name="email"
            placeholder=""
            type="email"
          />
          <Box flexDir="column">
            <PasswordInputWithValidation setIsInvalid={setIsInvalid} />
          </Box>
          <Spacer flex={{ base: 1, tablet: 0 }} />
          <Body
            as="span"
            color="black"
            mt={600}
            paddingX={{ base: 1, tablet: 900 }}
            pb={200}
            size={300}
            textAlign="center"
          >
            <Trans
              components={{
                TermsOfServiceLink: (
                  <LinkExternal
                    href={External.TERMS_OF_SERVICE.path}
                    textDecoration="underline"
                  />
                ),
                PrivacyPolicyLink: (
                  <LinkExternal
                    href={External.PRIVACY_POLICY.path}
                    textDecoration="underline"
                  />
                ),
              }}
              i18nKey="byCreatingAnAccountBold"
              t={t}
              values={{
                acceptAndContinue: t('onboarding.acceptAndContinue'),
              }}
            />
          </Body>
          <Button
            disabled={isInvalid}
            isLoading={isLoading}
            mb={400}
            mt={300}
            onClick={() => {
              buttonPressed({
                button_text: t('onboarding.acceptAndContinue'),
                page_or_screen_name: 'Onboarding-signup-email_input',
                feature: 'onboarding_reskin',
              });
            }}
            type="submit"
            width={1}
          >
            {t('onboarding.acceptAndContinue')}
          </Button>
        </Box>
      </HStack>
      <Box
        alignItems="center"
        display={{ base: 'none', tablet: 'flex' }}
        flexDirection="column"
        width={1}
      >
        <SkipSSR>
          <Box
            justifyContent="start"
            maxW={breakpoints.width500}
            paddingBottom={300}
            width={1}
          >
            {history.length > 1 && <BackButton onClick={goBack} />}
          </Box>
        </SkipSSR>
      </Box>
    </>
  );
};

export const SignupHomeowner = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const refreshSession = useRefreshSession();
  const { leadId } = usePostData();
  const partnerOrgId = usePartnerOrgId();

  const [createHomeownerUser, { loading: isOnboardingCreateHomeownerLoading }] =
    useOnboardingCreateHomeownerUserMutation();
  const [createProject, { loading: createProjectLoading }] =
    useCreateProjectMutation();

  const toast = useToast();
  const history = useHistory();

  const sendHubSpotEvent = useSendCustomHubSpotEvent();

  const validationSchema = makeHomeownerSignupValidationSchema(t);
  const initialValues = { password: '' };

  const showError = (description: string) => {
    toast({
      description,
      status: 'error',
      duration: 5000,
      isClosable: true,
      containerStyle: {
        maxWidth: '100%',
        whiteSpace: 'pre-wrap',
      },
    });
  };

  const onSubmit = async (formData: PasswordFormData) => {
    const { name, email, password } = formData;
    const nameTokens = name.split(' ');
    const firstName = nameTokens[0];
    const lastName = nameTokens[nameTokens.length - 1];

    if (!password || !email) return;

    buttonPressed({
      button_text: 'Accept and continue',
      page_or_screen_name: 'Email_input_password',
      feature: 'onboarding_reskin',
    });

    try {
      await createHomeownerUser({
        variables: {
          args: {
            user: {
              firstName,
              lastName,
              email,
              password,
            },
            ...(leadId && { leadId: Number(leadId) }),
            redirectTo: Routes.HOMEOWNER_APP_HOME.path,
          },
        },
        onCompleted: ({
          onboardingCreateHomeownerUser,
        }: OnboardingCreateHomeownerUserMutation) =>
          accountCreated({
            button_or_link: 'button',
            button_or_link_text: t('onboarding.acceptAndContinue'),
            page_or_screen_name: Page.SIGN_UP_ONBOARDING,
            primary_cta: true,
            platform_type: 'web',
            user_id: onboardingCreateHomeownerUser?.user?.id,
          }),
      });

      await sendHubSpotEvent({
        email,
        eventName: 'pe23226235_sign-up-event',
      });

      await refreshSession();

      const res = await createProject({
        variables: { autoCreated: true, orgId: partnerOrgId },
      });

      const projectId = res.data?.createProject.id;

      let redirectUrl = baseUrlToRedirect(
        window.location.href,
        Routes.HOMEOWNER_APP_HOME.path,
      );

      if (projectId) {
        redirectUrl = baseUrlToRedirect(
          window.location.href,
          generatePath(Route.HOMEOWNER_APP_PROJECT.path, {
            projectId,
          }),
        );
      }

      history.push(redirectUrl.pathname + redirectUrl.search);
    } catch (e) {
      const error = e as ApolloError;
      const graphQLErrors = error.graphQLErrors as GraphQLError[];
      const hasAlreadyAssociatedError = !!graphQLErrors?.find(({ message }) =>
        message.includes(EMAIL_IS_ALREADY_ASSOCIATED),
      );

      if (graphQLErrors) {
        if (hasAlreadyAssociatedError) {
          showError(EMAIL_IS_ALREADY_ASSOCIATED);
        } else {
          graphQLErrors.forEach(gqlError => {
            showError(gqlError.message);
          });
        }
      }
      logError(e as Error);
    }
  };

  return (
    <Box alignItems="stretch" flex={1} flexDirection="column" width={1}>
      <SharedForm<PasswordFormData>
        display="flex"
        flexGrow={1}
        initialValues={initialValues}
        marginX={500}
        marginY={{ base: 0, tablet: 400 }}
        onSubmit={onSubmit}
        transparentFieldset
        validationSchema={validationSchema}
      >
        <Fields
          isLoading={isOnboardingCreateHomeownerLoading || createProjectLoading}
        />
      </SharedForm>
    </Box>
  );
};
